import React, { useEffect, useState } from 'react';
import ellipsis from 'text-ellipsis';
import { Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { toast } from 'react-toastify';
import copy from 'copy-to-clipboard';
import qs from 'qs';
import { useToggle } from 'react-use';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { orderBy, keyBy, isEmpty, get } from 'lodash';
import { format as formatDate, } from 'date-fns';

import firebase from '../../firebase';
import { batch } from '../../shared/firebase';
import { canUpdateProductType } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import useTenant from '../hooks/useTenant';
import useFirebaseUser from '../hooks/useFirebaseUser';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import { fields } from '../../shared/models/textTemplate';
import { fields as groupFields } from '../../shared/models/textTemplateGroup';
import ModalButton from '../ModalButton';
import Select from 'react-select';

const db = firebase.firestore();
const { entries, } = Object;

const textTemplateGroupsRef = db.collection('textTemplateGroups');

export default function TextTemplatesModal(props) {
  const { isOpen, type, textTemplateKey, onClickClose, onApply, } = props;
  const tenant = useTenant();
  const [selectedGroups, setSelectedGroups] = useState([]);
  const textTemplates = useCollectionSubscriptionInTenant(db.collection('textTemplates').where('key', '==', textTemplateKey), [textTemplateKey]);
  const textTemplateGroups = useCollectionSubscriptionInTenant(textTemplateGroupsRef);
  const textTemplateGroupsById = keyBy(textTemplateGroups, 'id');
  const groupOptions = textTemplateGroups.map(_ => ({ label: _.name, value: _.id }));
  const { firebaseUser } = useFirebaseUser();
  
  const onCreateTextTemplateGroup = async (value) => {
    const ref = await textTemplateGroupsRef.add({ tenantId: tenant.id, name: value, createdAt: new Date() });
    return ref.id;
  };

  let filteredTextTemplates = textTemplates;
  if (!isEmpty(selectedGroups)) {
    filteredTextTemplates = filteredTextTemplates.filter(_ =>
      selectedGroups.some(group => (get(_, 'textTemplateGroupIds') || []).includes(group.value))
    );
  }
  
  const sortedTextTemplates = orderBy(filteredTextTemplates, _ => _.createdAt.toDate(), 'desc');
  const replaceKeywords = (body) => {
    return body
      .replace('{{LOGIN_USER_NAME}}', firebaseUser.displayName)
  };

  return (
    <Modal isOpen={isOpen} style={{ minWidth: '80vw', }}>
      <div className='modal-header'>
        <div className='w-100 d-flex justify-content-between'>
          <h5 className='modal-title'>
            文例
          </h5>
        </div>
      </div>
      <ModalBody>
        <div>
          <div className='d-flex justify-content-between flex-wrap gap-2 mb-3'>
            <div style={{ width: 300 }}>
              <label className="small text-muted">グループで絞込み</label>
              <Select
                isMulti
                options={groupOptions}
                value={selectedGroups}
                onChange={setSelectedGroups}
                placeholder="グループを選択..."
                isClearable
              />
            </div>
            <div className='d-flex justify-content-end align-items-end gap-2'>
              <ModalButton
                title="グループ管理"
                content={_ => <TextTemplateGroups firebaseUser={firebaseUser} textTemplateGroups={textTemplateGroups} />}
                modalProps={{ style: { minWidth: 800 } }}
              >
                グループ管理
              </ModalButton>
              <AddInTenantButton
                itemRef={db.collection('textTemplates').doc()}
                processValues={(_) => ({ ..._, key: textTemplateKey, })}
                FormModal={ModelFormModal}
                formProps={{ 
                  title: '文例追加', 
                  fields: fields({ type, textTemplateGroups, onCreateTextTemplateGroup }), 
                }}
              />
            </div>
          </div>
          <table className='table'>
            <thead className='thead-light text-nowrap text-center'>
              <tr>
                <th style={{ width: 50, }}>グループ</th>
                <th style={{ width: 50 }}>タイトル</th>
                <th style={{ width: 200 }}>内容</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedTextTemplates.map((textTemplate) => {
                const { id, ref, subject, body, createdAt, textTemplateGroupIds = [] } = textTemplate;
                const onClickApply = () => {
                  if(!window.confirm('本当に適用しますか？')) return;

                  onApply(replaceKeywords(body));
                  onClickClose();
                };

                return (
                  <tr key={id}>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {textTemplateGroupIds.map(_ => (
                          <div key={_} className="badge badge-secondary">{textTemplateGroupsById[_]?.name}</div>
                        ))}
                      </div>
                    </td>
                    <td>
                      {subject}
                      <div className="small text-muted">{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</div>
                    </td>
                    <td>{ellipsis(body, 100)}</td>
                    <td>
                      <div className="d-flex text-nowrap flex-column gap-1">
                        <Button size="sm" onClick={onClickApply}>
                          適用
                        </Button>
                        <EditButton
                          size="sm"
                          itemRef={ref}
                          FormModal={ModelFormModal}
                          formProps={{ 
                            title: '文例編集', 
                            fields: fields({ type, textTemplateGroups, onCreateTextTemplateGroup }), 
                          }}
                        />
                        <DeleteButton
                          size="sm"
                          itemRef={ref}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className='cancel' color='secondary' onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
    </Modal>
  );
};

function TextTemplateGroups(props) {
  const { firebaseUser, textTemplateGroups = [] } = props;
  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <AddInTenantButton
          itemRef={textTemplateGroupsRef.doc()}
          FormModal={ModelFormModal}
          formProps={{ title: 'グループ追加', fields: groupFields() }}
        />
      </div>
      <table className="table">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 200 }}>グループ名</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {textTemplateGroups.map((textTemplateGroup) => {
            const { id, ref, name } = textTemplateGroup;
            return (
              <tr key={id}>
                <td>{name}</td>
                <td className="text-nowrap text-right">
                  <EditButton
                    itemRef={ref}
                    className="ml-1"
                    FormModal={ModelFormModal}
                    formProps={{ documentName: 'textTemplateGroup', title: 'グループ編集', fields: groupFields() }}
                  />
                  <DeleteButton itemRef={ref} className="ml-1" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
